if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.initAnsonColorFilter();
      }

      disconnectedCallback() {
        this.ansonColorFilterUnsubscriber?.();
      }

      initAnsonColorFilter() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        const initialColor = this.dataset.ansonInitialColor;
        if (initialColor) this.filterMediaByColor(initialColor);

        // Which option slot (option1/2/3) is 'Color' on this product, resolved server-side
        // by name so it survives the merchant reordering options (e.g. Color before Size).
        const colorOptionIndex = this.dataset.ansonColorOptionIndex;

        this.ansonColorFilterUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          if (!variant || !colorOptionIndex) return;
          const color = variant['option' + colorOptionIndex];
          if (color) this.filterMediaByColor(color);
        });
      }

      filterMediaByColor(color) {
        const normalizedColor = color.trim().toLowerCase();
        const allTaggedSlides = this.querySelectorAll('[data-media-color]');

        const taggedColors = new Set(
          Array.from(allTaggedSlides)
            .map((el) => el.dataset.mediaColor?.trim().toLowerCase())
            .filter(Boolean)
        );
        // If nothing is tagged for this color, don't filter (avoid hiding everything).
        if (!taggedColors.has(normalizedColor)) return;

        let firstVisibleMediaId = null;

        allTaggedSlides.forEach((el) => {
          const mediaColor = el.dataset.mediaColor?.trim().toLowerCase();
          // Untagged media (blank color) always stays visible regardless of type.
          const shouldShow = !mediaColor || mediaColor === normalizedColor;
          el.classList.toggle('anson-color-hidden', !shouldShow);

          if (shouldShow && !firstVisibleMediaId) {
            firstVisibleMediaId = el.dataset.mediaId || el.dataset.target;
          }
        });

        // If the currently active slide just got hidden, jump to the first visible one.
        const activeSlide = this.elements.viewer?.querySelector('[data-media-id].is-active');
        if (activeSlide && activeSlide.classList.contains('anson-color-hidden') && firstVisibleMediaId) {
          this.setActiveMedia(firstVisibleMediaId, false);
        }

        if (this.elements.viewer?.slider) this.elements.viewer.resetPages?.();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
